import { useCallback, useMemo, useRef, type CSSProperties } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import type {
  ColDef,
  ColumnResizedEvent,
  DragStoppedEvent,
  GridApi,
  SortChangedEvent,
  ViewportChangedEvent,
} from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../../shared/ui/ag-grid-theme.css'
import { AG_GRID_LOCALE_RU } from '../../../shared/ag-grid/locale'
import { useGridSelection } from '../../../shared/hooks/useGridSelection'
import { CheckboxSetFilter } from '../../table/CheckboxSetFilter'
import { useAuthStore } from '../../auth/authStore'
import { useOrderColState } from '../../orders/hooks/useOrderColState'

ModuleRegistry.registerModules([AllCommunityModule])

interface Props<T extends { id: string }> {
  rowData: T[]
  columnDefs: ColDef<T>[]
  /** Базовый ключ вида колонок: `crm_<entity>_cols_v<N>` — к нему добавится id пользователя. */
  storageKey: string
  /** Высота обёртки — классом Tailwind или стилем (для таблиц «по числу строк»). */
  className?: string
  style?: CSSProperties
  noRowsText: string
  onViewportChanged?: (e: ViewportChangedEvent<T>) => void
}

/**
 * Таблица раздела «Запросы брокеру» по контракту docs/ui-conventions.md §3:
 * только чтение, выделение и копирование ячеек, фильтры-галочки, вид колонок
 * сохраняется для каждого пользователя.
 */
export function BrokerGrid<T extends { id: string }>({
  rowData,
  columnDefs,
  storageKey,
  className = 'h-[calc(100vh-260px)]',
  style,
  noRowsText,
  onViewportChanged,
}: Props<T>) {
  const userId = useAuthStore((s) => s.user?.id)
  const gridRef = useRef<AgGridReact<T>>(null)
  const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection<T>(gridRef)
  const { saveState, onGridReady } = useOrderColState(storageKey, userId)
  const save = useCallback((api: GridApi) => saveState(api), [saveState])

  const defaultColDef = useMemo<ColDef<T>>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: false,
      filter: CheckboxSetFilter,
      floatingFilter: false,
      cellStyle: getCellStyle,
    }),
    [getCellStyle],
  )

  return (
    <div
      className={`crm-table ag-theme-alpine rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm ${className}`}
      style={style}
    >
      <AgGridReact<T>
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        localeText={AG_GRID_LOCALE_RU}
        getRowId={(p) => String(p.data.id)}
        onGridReady={onGridReady}
        onCellMouseDown={onCellMouseDown}
        onCellMouseOver={onCellMouseOver}
        onDragStopped={(e: DragStoppedEvent<T>) => save(e.api)}
        onColumnResized={(e: ColumnResizedEvent<T>) => {
          if (e.finished) save(e.api)
        }}
        onSortChanged={(e: SortChangedEvent<T>) => save(e.api)}
        onViewportChanged={onViewportChanged}
        rowHeight={40}
        headerHeight={40}
        suppressDragLeaveHidesColumns
        suppressCellFocus
        overlayNoRowsTemplate={`<span class="text-sm text-gray-400">${noRowsText}</span>`}
      />
    </div>
  )
}

/** Высота таблицы «по числу строк»: маленький запрос — компактная таблица, большой — со скроллом. */
export function gridHeightForRows(rows: number, maxPx = 560): CSSProperties {
  const ROW = 40
  const HEADER = 40
  const BORDER = 2
  return { height: Math.min(Math.max(rows, 3) * ROW + HEADER + BORDER, maxPx) }
}
